/**
 * Task 214 — corpus-scoped markdown renderer.
 *
 * Wraps the shared AnswerMarkdown: bracket headings (`### [...]`) become
 * on-brand section markers (verse kickers, serif topic heads, whisper-level
 * continuation dividers) while every other line renders through the exact same
 * safe markdown pipeline the chat uses. Chat answers never pass through here,
 * so their rendering stays byte-identical.
 *
 * Marker language (extends the Task 208/209 armory system):
 *   verse         gold small-caps kicker + short arrow hairline — the chapter's
 *                 scannable landmarks
 *   topical       serif heading with a gold hairline accent
 *   structural    same, dimmed when meta (NO-ENSEÑANZA, Q&A)
 *   continuation  NO text — a centered fading hairline with the bow sigil; the
 *                 reader just feels a breath between segments
 *   resume        a soft "↳ continúa · {topic}" thread marker, not a heading
 */
import type { JSX } from "react";
import { AnswerMarkdown } from "../components/AnswerMarkdown";
import { BowSigil } from "../components/Wordmark";
import { parseCorpusBlocks, type CorpusBlock, type CorpusHeading } from "./heading-presentation";
import { prismKey, sectionMatchesFilter, type PrismIndex } from "./prism";

function SectionHeading(props: { heading: CorpusHeading }): JSX.Element {
  const { heading } = props;
  switch (heading.kind) {
    case "continuation":
      // Mechanical chunk boundary: a breath, not a banner.
      return (
        <div className="creader-sect creader-sect--continuation" aria-hidden="true">
          <span className="creader-sect__line" />
          <BowSigil className="creader-sect__sigil" />
          <span className="creader-sect__line creader-sect__line--mirror" />
        </div>
      );
    case "resume":
      return (
        <p className="creader-sect creader-sect--resume">
          <span aria-hidden="true">↳ </span>
          continúa · <span className="creader-sect__resume-topic">{heading.resumeLabel}</span>
        </p>
      );
    case "verse":
      return (
        <h3 className="creader-sect creader-sect--verse">
          <span className="creader-sect__verse-line" aria-hidden="true" />
          <span className="creader-sect__verse-ref">{heading.text}</span>
        </h3>
      );
    case "structural":
    case "topical":
      return (
        <h3 className={`creader-sect creader-sect--topic${heading.meta === true ? " is-meta" : ""}`}>
          {heading.text}
        </h3>
      );
  }
}

/** Prism chips rendered beside a section heading (Task 220 POC). */
function PrismChips(props: { slugs: string[]; prism: PrismIndex }): JSX.Element {
  return (
    <span className="prism-chips">
      {props.slugs.map((slug) => {
        const def = props.prism.defBySlug.get(slug);
        if (def === undefined) return null;
        return (
          <span
            key={slug}
            className="prism-chip"
            style={{ ["--prism-hue" as string]: def.hue }}
            title={`${def.hebrew} · ${def.display_es}`}
          >
            <span className="prism-chip__glyph">{def.glyph}</span>
            {def.hebrew}
          </span>
        );
      })}
    </span>
  );
}

/** Blocks grouped per heading, so a section can carry layers + filter dimming. */
interface SectionGroup {
  heading: CorpusHeading | null;
  blocks: Array<{ block: CorpusBlock; index: number }>;
}

export function CorpusMarkdown(props: {
  text: string;
  /** Task 220 POC: the chunk's reader anchor + prism data + active filter. */
  anchor?: string;
  prism?: PrismIndex | null;
  activeLayers?: ReadonlySet<string>;
}): JSX.Element {
  const { text, anchor, prism, activeLayers } = props;
  const blocks = parseCorpusBlocks(text);
  // The first prose block carries the manuscript drop cap (Task 209) — mark it
  // explicitly since headings may precede it and CSS can't express "first md".
  const firstMd = blocks.findIndex((b) => b.type === "md");

  const groups: SectionGroup[] = [];
  for (const [index, block] of blocks.entries()) {
    if (block.type === "heading") groups.push({ heading: block.heading, blocks: [{ block, index }] });
    else if (groups.length === 0) groups.push({ heading: null, blocks: [{ block, index }] });
    else (groups[groups.length - 1] as SectionGroup).blocks.push({ block, index });
  }

  return (
    <>
      {groups.map((group, g) => {
        const layers =
          prism != null && anchor !== undefined && group.heading !== null
            ? prism.byKey.get(prismKey(anchor, group.heading.text)) ?? []
            : [];
        const dimmed =
          activeLayers !== undefined && activeLayers.size > 0 && !sectionMatchesFilter(layers, activeLayers);
        return (
          <section
            // eslint-disable-next-line react/no-array-index-key
            key={g}
            className={`creader-psec${dimmed ? " is-dimmed" : ""}`}
          >
            {group.blocks.map(({ block, index }) =>
              block.type === "heading" ? (
                <div key={index} className="creader-psec__head">
                  <SectionHeading heading={block.heading} />
                  {layers.length > 0 && prism != null ? <PrismChips slugs={layers} prism={prism} /> : null}
                </div>
              ) : (
                <div
                  key={index}
                  className={index === firstMd ? "creader-md__block creader-md__lead" : "creader-md__block"}
                >
                  <AnswerMarkdown text={block.md} />
                </div>
              ),
            )}
          </section>
        );
      })}
    </>
  );
}
